import React, { useState, useEffect, useContext, useCallback } from 'react';
import { withRouter } from 'react-router';
import PropTypes from 'prop-types';
import { compose } from 'react-apollo';
import ConfirmDialog from 'chameleon/packages/blue-book-player/library/modal/ConfirmDialog';
import LoadingSpinner from 'chameleon/packages/blue-book-player/library/LoadingSpinner';
import SubmitSuccessContent from 'chameleon/packages/blue-book-player/InnerContainer/SubmitConfirmDialog/SubmitSuccessContent';
import UnAnsweredQuestions from 'chameleon/packages/blue-book-player/InnerContainer/SubmitConfirmDialog/UnAnsweredQuestions';
import { BluebookContext } from 'chameleon/packages/blue-book-player/state/BluebookContext';
import areMcqQuestions from 'chameleon/packages/blue-book-player/utility/helpers/areMcqQuestions';
import { getQuestionsAnswerState } from 'chameleon/packages/blue-book-player/utils';
import { withAssignmentSubmit } from 'chameleon/ui-stack/hocs/player';
import {
  SET_IS_REVIEW_PAGE,
  SET_SUBMISSION_READY,
  SET_IS_QUIZ_SUBMITTED,
} from 'chameleon/packages/blue-book-player/state/reducer';
import {
  learnositySubmit,
  learnosityExitPlayer,
} from 'chameleon/packages/blue-book-player/utility/learnosityUtils/submit';

const SubmitConfirmDialog = (props) => {
  const {
    learnosityApp,
    isImpersonating,
    setShowSubmitConfirmDialog,
    setCurrentItemIndex,
    location: { state },
    params: { subject, assignmentId },
    router,
    submitAssignment,
    isDigitalExam,
  } = props;

  const { state: playerState, dispatch } = useContext(BluebookContext);
  const { questions } = playerState;
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [unansweredQuestions, setUnansweredQuestions] = useState(null);
  const [isExitButtonDisabled, setIsExitButtonDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [modalContent, setModalContent] = useState({});
  const [isPrimaryBtnDisabled, setIsPrimaryBtnDisabled] = useState(false);
  const [questionLength, setQuestionLength] = useState(0);

  const FOOTER_INFO = 'Are you sure you want to submit your assessment?';
  const EXPLANATORY_INFO =
    'On your AP Exam, there will not be a Submit button. You will have to wait until time expires, at which point your answers will be submitted automatically.';
  const navigateBackFromReviewPage = () => {
    dispatch({ type: SET_IS_REVIEW_PAGE, payload: false });
  };

  const submissionReadyCallback = (submissionReady) => {
    dispatch({ type: SET_SUBMISSION_READY, payload: submissionReady });
    setIsExitButtonDisabled(!submissionReady);
  };

  const handleSubmit = () => {
    learnositySubmit(learnosityApp, submissionReadyCallback, isImpersonating);
    !isImpersonating && submitAssignment(assignmentId);
    setIsSubmitted(true);
  };

  const handleClose = () => {
    setShowSubmitConfirmDialog(false);
  };

  const onExitPlayer = () => {
    learnosityExitPlayer(subject, router, state);
  };

  /**
   * Obtains the position (zero based index) of a given item.
   * see https://docs.learnosity.com/assessment/items/methods#assessApp-GetItemPosition.itemReference
   */
  const getQuestionPosition = useCallback(
    (questionRef) => learnosityApp.assessApp().getItemPosition(questionRef),
    [learnosityApp],
  );

  const onNavigateToQuestion = useCallback(
    (questionRef) => {
      navigateBackFromReviewPage();
      setShowSubmitConfirmDialog(false);
      setCurrentItemIndex(getQuestionPosition(questionRef));
      learnosityApp?.items()?.goto(questionRef);
    },
    [
      setShowSubmitConfirmDialog,
      setCurrentItemIndex,
      getQuestionPosition,
      learnosityApp,
    ],
  );

  const checkUnansweredQuestions = () => {
    const responses = learnosityApp?.getResponses();
    const learnosityItems = learnosityApp?.getItems();
    const learnosityQuestions = Object.keys(learnosityItems || {});

    const questionsAnswerState = getQuestionsAnswerState(
      learnosityItems,
      learnosityQuestions,
      responses,
    );

    const unansweredQuestionIds = questionsAnswerState
      .filter((question) => !question.isAnswered)
      .map((question) => question.id);

    setUnansweredQuestions(unansweredQuestionIds);
  };

  useEffect(() => {
    if (!playerState.isTestReady) return;
    const items = learnosityApp.getItems();
    const totalQuestionsLength = Object.keys(items).reduce(
      (acc, key) => acc + items[key].questions.length,
      0,
    );
    const loadedQuestionsLength = Object.keys(
      learnosityApp.getQuestions(),
    ).length;

    if (loadedQuestionsLength !== totalQuestionsLength) {
      learnosityApp.on('items:lazyload:complete', checkUnansweredQuestions);
    } else {
      checkUnansweredQuestions();
    }

    submissionReadyCallback(false);

    return () => {
      learnosityApp.off('items:lazyload:complete', checkUnansweredQuestions);
    };
  }, [playerState.isTestReady]);

  useEffect(() => {
    setIsLoading(unansweredQuestions === null);
    setQuestionLength(unansweredQuestions && unansweredQuestions.length);
  }, [unansweredQuestions]);

  useEffect(() => {
    setIsPrimaryBtnDisabled(
      isSubmitted
        ? isLoading && isExitButtonDisabled && !isImpersonating
        : isLoading,
    );

    setModalContent(
      isSubmitted
        ? {
            btnPrimary: 'Exit',
            btnPrimaryAria: { 'data-test-button-exit': true },
          }
        : {
            btnPrimary: 'Yes',
            btnSecondary: 'No',
          },
    );
    if (isSubmitted) {
      dispatch({ type: SET_IS_QUIZ_SUBMITTED, payload: true });
    }
  }, [isSubmitted, isLoading, isExitButtonDisabled, isImpersonating]);

  return (
    <ConfirmDialog
      size="small"
      title="Submit"
      onSuccess={isSubmitted ? onExitPlayer : handleSubmit}
      onClose={isSubmitted ? onExitPlayer : handleClose}
      content={modalContent}
      isPrimaryBtnDisabled={isPrimaryBtnDisabled}
      aria={{
        'aria-live': 'polite',
      }}
    >
      {isLoading ? (
        <LoadingSpinner data-test-learnosity-loading />
      ) : isSubmitted ? (
        <SubmitSuccessContent areMcqQuestions={areMcqQuestions(questions)} />
      ) : (
        <div className="flex flex-col gap-8">
          {questionLength > 0 && (
            <UnAnsweredQuestions
              questions={unansweredQuestions}
              onNavigateToQuestion={onNavigateToQuestion}
              getQuestionPosition={getQuestionPosition}
            />
          )}
          <div className="flex flex-col gap-4">
            <p className="m-0">{FOOTER_INFO}</p>
            {isDigitalExam && (
              <p className="m-0 text-[12px] italic leading-[18px] text-gray-600">
                {EXPLANATORY_INFO}
              </p>
            )}
          </div>
        </div>
      )}
    </ConfirmDialog>
  );
};

SubmitConfirmDialog.propTypes = {
  learnosityApp: PropTypes.object.isRequired,
  isImpersonating: PropTypes.bool.isRequired,
  setShowSubmitConfirmDialog: PropTypes.func.isRequired,
  setCurrentItemIndex: PropTypes.func,
  isDigitalExam: PropTypes.bool.isRequired,
};

SubmitConfirmDialog.defaultProps = {
  setCurrentItemIndex: () => {},
};

export default compose(withRouter, withAssignmentSubmit)(SubmitConfirmDialog);
